/**
 * FirstLoginBanner — role-specific orientation banner shown on a user's
 * first login (Task #60, Jun 8 2026).
 *
 * On mount, calls onboarding:status. If firstLoginCompleted is true the
 * component renders nothing. Otherwise it renders a dismissible banner
 * with role-specific welcome copy explaining the daily workflow surface
 * and (for PEM + physician roles) the explicit role boundary from
 * Carlos's spec — "The Physician Diagnoses. The PEM Coordinates."
 *
 * Dismissal is optimistic: local state hides the banner immediately,
 * the onboarding:complete IPC fires in the background, and any failure
 * is logged but doesn't block the user.
 *
 * The banner is intentionally lightweight — text card + dismiss button —
 * no overlay, no arrows pointing at sidebar items. The arrow-style
 * coach mark is a polish-tier upgrade tracked separately if needed.
 */
import React, { useState, useEffect, useCallback } from "react";
import { X, Sparkles, AlertCircle } from "lucide-react";

const COPY = {
  patient_experience: {
    title: "Welcome to your Patient Experience workspace",
    body: (
      <>
        You own the patient relationship from first lead to long-term wellness.
        Your day-to-day workflow lives on the <strong>Patient Experience</strong>{" "}
        dashboard in the sidebar — journey stages, follow-ups, concierge messaging.
        The physician owns the medical decision. If you find yourself making
        clinical recommendations, route to the physician.
      </>
    ),
  },
  physician: {
    title: "Welcome to your Physician workspace",
    body: (
      <>
        You own the medical evaluation, candidacy verdict, safety screening,
        and treatment plan. Your day-to-day workflow lives on the{" "}
        <strong>Physician Dashboard</strong> in the sidebar — the consult queue
        and patient charts. The PEM owns scheduling, pricing, and follow-up. If
        you find yourself answering pricing or scheduling questions, route to
        the PEM.
      </>
    ),
  },
  nurse: {
    title: "Welcome to your Clinical workspace",
    body: (
      <>
        Your workflow lives across <strong>Patients</strong>, <strong>Vitals</strong>,
        and <strong>Medications</strong> in the sidebar. Clinical Documentation
        houses SOAP notes and chart entries. If a patient asks about pricing,
        scheduling, or travel, route them to the Patient Experience Manager.
      </>
    ),
  },
  sysadmin: {
    title: "Welcome — you have full system access",
    body: (
      <>
        You can manage users, roles, and permissions under <strong>Access Control</strong>.
        The Patient Experience Manager and Physician dashboards are also visible
        to you for support purposes. Audit Log and Compliance Posture are under
        Administration.
      </>
    ),
  },
  admin: {
    title: "Welcome to the admin workspace",
    body: (
      <>
        Manage users and roles under <strong>Access Control</strong>. Daily team
        oversight lives in the Compliance Posture and Audit Log sections.
      </>
    ),
  },
  manager: {
    title: "Welcome to the management workspace",
    body: (
      <>
        Your team performance and KPI rollups live across the various dashboards.
        Patient Experience and Physician dashboards both show practice-level
        rollups for review.
      </>
    ),
  },
  sales: {
    title: "Welcome to your sales workspace",
    body: (
      <>
        Your day starts on <strong>My Day</strong> in the CRM — your personal queue
        of leads, follow-ups, and tasks. Once a lead becomes a patient candidate,
        the Patient Experience Manager takes over coordination.
      </>
    ),
  },
};

const GENERIC_FALLBACK = {
  title: "Welcome to ZignaGenix",
  body: (
    <>
      Use the sidebar on the left to navigate the platform. Your role determines
      which surfaces are visible to you. If anything looks off, contact your
      administrator.
    </>
  ),
};

export default function FirstLoginBanner({ user }) {
  const [show, setShow] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!user) {
        setChecked(true);
        return;
      }
      try {
        const r = await window.zignagenixAPI?.invoke?.("onboarding:status");
        if (cancelled) return;
        if (r && !r.error && !r.firstLoginCompleted) {
          setShow(true);
        }
      } catch (e) {
        // If the channel isn't available yet (e.g. migration not run),
        // silently skip. We don't want the banner to be a hard dependency.
        // eslint-disable-next-line no-console
        if (process.env.NODE_ENV !== "production") {
          console.debug("[FirstLoginBanner] onboarding:status skipped:", e?.message);
        }
      } finally {
        if (!cancelled) setChecked(true);
      }
    }
    check();
    return () => { cancelled = true; };
  }, [user]);

  const dismiss = useCallback(async () => {
    setShow(false); // optimistic hide
    try {
      await window.zignagenixAPI?.invoke?.("onboarding:complete");
    } catch (e) {
      // Failure is non-fatal — banner stays hidden in this session, and
      // worst case it shows again next login. Better than a confusing
      // "couldn't dismiss" error.
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV !== "production") {
        console.debug("[FirstLoginBanner] onboarding:complete failed:", e?.message);
      }
    }
  }, []);

  if (!checked || !show) return null;

  // Map role to copy. Treat 'concierge' as sales for now since concierge is
  // a CRM-side role. Pick the most specific match; fallback to generic.
  const role = user?.role || "";
  const copy = COPY[role]
    || (role === "concierge" ? COPY.sales : null)
    || GENERIC_FALLBACK;

  return (
    <div
      role="region"
      aria-label="First-login orientation"
      style={{
        position: "relative",
        margin: "16px 24px 8px",
        padding: "14px 18px",
        background: "linear-gradient(135deg, #E6F7F9 0%, #F0F4F8 100%)",
        border: "1px solid #2E8B98",
        borderRadius: 12,
        boxShadow: "0 2px 8px rgba(46, 139, 152, 0.12)",
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
      }}
    >
      <div style={{
        background: "#2E8B98", color: "white",
        width: 36, height: 36, borderRadius: 10,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <Sparkles size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 14, fontWeight: 700, color: "#212529", marginBottom: 4,
        }}>
          {copy.title}
        </div>
        <div style={{ fontSize: 12, color: "#495057", lineHeight: 1.5 }}>
          {copy.body}
        </div>
        {(role === "physician" || role === "patient_experience") && (
          <div style={{
            marginTop: 8, padding: "6px 10px",
            background: "rgba(46, 139, 152, 0.08)", borderRadius: 6,
            fontSize: 11, fontStyle: "italic", color: "#2E8B98",
          }}>
            <AlertCircle size={11} style={{ verticalAlign: "middle" }} />{" "}
            ZignaGenix principle: <strong>The Physician Diagnoses. The PEM Coordinates.</strong>
          </div>
        )}
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss orientation banner"
        title="Got it — don't show this again"
        style={{
          background: "white",
          border: "1px solid #DEE2E6",
          color: "#6C757D",
          padding: "5px 12px",
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          flexShrink: 0,
        }}
      >
        <X size={12} /> Got it
      </button>
    </div>
  );
}
